import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { auth } from '../../config/firebase';
import { SocialIcon } from '../icons/SocialIcon';
import { useSocialPlatforms } from '../../hooks/useSocialPlatforms';
import type { SocialPlatform } from '../icons/SocialIcon';
import { InstagramOptionsModal } from './InstagramOptionsModal';

interface ConnectAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConnect: (platform: SocialPlatform) => void;
  existingPlatforms?: string[];
}

export const ConnectAccountModal: React.FC<ConnectAccountModalProps> = ({
  isOpen,
  onClose,
  onConnect,
  existingPlatforms = []
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [isInstagramOptionsModalOpen, setIsInstagramOptionsModalOpen] = useState(false);
  const { allPlatforms } = useSocialPlatforms();
  const queryClient = useQueryClient();

  if (!isOpen) return null;

  const executePlatformConnection = async (platform: SocialPlatform) => {
    try {
      setIsLoading(platform);
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      // Debug: Log token info
      console.log('🔍 Firebase token length:', token.length);
      console.log('🔍 Firebase token starts with:', token.substring(0, 50) + '...');
      console.log('🔍 Firebase user:', auth.currentUser?.uid);
      console.log('🔍 Firebase user email:', auth.currentUser?.email);

      // Map platform to API endpoint - Facebook uses 'meta' endpoint
      const getApiEndpoint = (platform: SocialPlatform): string => {
        if (platform === 'facebook') {
          return 'meta';
        }
        return platform;
      };
      
      const formattedPlatform = getApiEndpoint(platform);
      
      console.log(`Connecting to platform: ${platform}, API endpoint: management/accounts/${formattedPlatform}/config`);

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Debug: Log headers
      console.log('🔍 Request headers:', headers);
      console.log('🔍 Authorization header:', headers.Authorization.substring(0, 50) + '...');

      const response = await fetch(`https://api.socialness.ai/management/accounts/${formattedPlatform}/config`, {
        headers
      });

      console.log('🔍 Response status:', response.status);
      console.log('🔍 Response headers:', Object.fromEntries([...response.headers.entries()]));

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Platform configuration request failed with status: ${response.status}`);
        console.error(`Error details:`, errorText);
        throw new Error(`Failed to get platform configuration: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success && data.authUrl) {
        queryClient.removeQueries({ queryKey: ['accounts'] });
        queryClient.invalidateQueries({ queryKey: ['accounts'] });
        
        localStorage.setItem('socialness_oauth_pending', 'true');
        localStorage.setItem('socialness_oauth_platform', platform);
        localStorage.setItem('socialness_oauth_timestamp', Date.now().toString());
        
        window.location.href = data.authUrl;
      } else {
        throw new Error('Invalid platform configuration response: Missing authUrl');
      }
    } catch (error) {
      console.error('Error connecting to platform:', error);
      let errorMessage = 'Failed to connect to platform. Please try again.';
      if (error instanceof Error) {
        if (error.message.includes('401') || error.message.includes('Unauthorized')) {
          errorMessage = 'Authentication failed. Please log out and log back in to try again.';
        } else if (error.message.includes('404') || error.message.includes('Not Found')) {
          errorMessage = `The ${platform} connection service is currently unavailable. Please try again later.`;
        } else if (error.message.includes('500')) {
          errorMessage = 'Server error occurred. Our team has been notified. Please try again later.';
        } else if (error.message.includes('Network Error') || error.message.includes('Failed to fetch')) {
          errorMessage = 'Network connection issue. Please check your internet connection and try again.';
        }
        console.error('Detailed error:', error.message);
      }
      alert(errorMessage);
      
      // Reason: Only close modal and reset loading state on error, not on successful redirect
      setIsLoading(null);
      if (platform === 'instagram' || platform === 'facebook') {
         setIsInstagramOptionsModalOpen(false);
      }
    }
  };

  const handleSocialPlatformSelect = async (platform: SocialPlatform) => {
    if (platform === 'instagram') {
      setIsInstagramOptionsModalOpen(true); 
    } else {
      await executePlatformConnection(platform);
    }
  };
  
  const handleCloseInstagramOptionsModal = () => {
    setIsInstagramOptionsModalOpen(false);
  };

  return (
    <>
      <div className={`fixed inset-0 z-50 overflow-y-auto ${isOpen ? 'block' : 'hidden'}`}>
        <div className="flex min-h-screen items-center justify-center p-4">
          <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
          
          <div className="relative bg-white rounded-xl border border-black shadow-xl max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">Connect Social Network</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {allPlatforms.map(platform => {
                const isDisabled = platform.status === 'coming-soon';
                const isConnecting = isLoading === platform.id; 
                return (
                  <button
                    key={platform.id}
                    onClick={() => !isDisabled && handleSocialPlatformSelect(platform.id)}
                    disabled={isDisabled || isConnecting || (isLoading !== null && isLoading !== platform.id) }
                    className={`flex items-center gap-2 p-3 rounded-lg border ${
                      isDisabled
                        ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                        : 'border-black hover:border-primary-600'
                    }`}
                  >
                    <SocialIcon platform={platform.id} size={24} />
                    <div className="flex flex-col items-start">
                      <span className={`text-sm ${isDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
                        {platform.name}
                      </span>
                      {platform.status === 'coming-soon' && (
                        <span className="text-xs text-gray-400">
                          Coming soon
                        </span>
                      )}
                      {isConnecting && (
                        <span className="text-xs text-primary-600">
                          Connecting...
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {isInstagramOptionsModalOpen && (
        <InstagramOptionsModal
          isOpen={isInstagramOptionsModalOpen}
          onClose={handleCloseInstagramOptionsModal}
          onConnectViaFacebook={() => executePlatformConnection('facebook')}
          onConnectDirectlyInstagram={() => executePlatformConnection('instagram')}
          isConnecting={isLoading === 'facebook' || isLoading === 'instagram'}
          connectingPlatform={isLoading}
        />
      )}
    </>
  );
};